// src/state/useDocsTree.js
import { useState, useEffect, useCallback } from 'react';
import { getItems, moveItem, buildTree } from '../helpers/projectHelpers';
import * as dragHelpers from '../helpers/dragHelpers';
import { debug } from '../helpers/debug';
import { apiGetProject } from '../api/projects';
import { findItem } from '../helpers/mapKey';

export default function useDocsTree({ user, currentProject, tabs, setTabs, setActiveTabId, setLeftActiveTabId, setRightActiveTabId }) {
  const [itemsMap, setItemsMap] = useState(new Map()); // Map is single source of truth
  const [itemsArr, setItemsArr] = useState([]);        // Render tree
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [authorized, setAuthorized] = useState(null);  // null = not checked, true = authorized, false = unauthorized

  // Use only the explicitly provided currentProject.id. Do not fall back to
  // user.projects[0] as this can cause the wrong project's content to load.
  const projectId = currentProject?.id ?? null;

  //console.log("Project ID:", projectId);

  // ---------------- Refresh / Load Items ----------------
  const refresh = useCallback(async () => {
    if (!projectId) {
      setItemsMap(new Map());
      setItemsArr([]);
      return;
    }
    try {
      // Verify project permissions server-side first. If the current user is not
      // authorized to access this project, refuse to load items.
      const project = await apiGetProject(projectId);
      
      // If project is returned, user is authorized by RLS.
      let allowed = Boolean(project);
      
      // If project is null (RLS blocked it), double-check by fetching user's projects
      if (!allowed && user) {
        const { apiGetProjects } = await import('../api/projects');
        const userProjects = await apiGetProjects(user.id, user.email);
        allowed = userProjects && userProjects.some(p => Number(p.id) === Number(projectId));
      }

      if (!allowed) {
        console.warn('[useDocsTree] unauthorized access attempt to project', projectId, 'for user', user?.id);
        setAuthorized(false);
        setItemsMap(new Map());
        setItemsArr([]);
        return;
      }

      setAuthorized(true);

      const { itemsMap: map } = await getItems(projectId);
      const newMap = map instanceof Map ? map : new Map(Object.entries(map));
      setItemsMap(newMap);
      setItemsArr(buildTree([...newMap.values()]));
    } catch (err) {
      debug('[useDocsTree] refresh error:', { type: 'error' }, err);
      setAuthorized(false);
      setItemsMap(new Map());
      setItemsArr([]);
    }
  }, [projectId, user]);

  useEffect(() => {
    // Load items whenever the selected projectId changes. If there's no project,
    // clear the tree. Previously this only refreshed when the user had exactly
    // one project which caused empty views when multiple projects existed.
    if (projectId) {
      refresh();
    } else {
      setItemsMap(new Map());
      setItemsArr([]);
      setDraggedId(null);
      setDragOverId(null);
    }
  }, [projectId, refresh]);

  useEffect(() => {
    setItemsArr(buildTree([...itemsMap.values()]));
  }, [itemsMap]);

  // ---------------- Drag & Drop Wrappers ----------------
  const dragStart = useCallback((e, id, kind, metadata = null) => {
    dragHelpers.dragStart(e, id, setDraggedId, kind, metadata);
  }, []);

  const dragOver = useCallback((e, item, expandFolder) => {
    dragHelpers.dragOver(e, item, setDragOverId, expandFolder);
  }, []);

  const dragEnd = useCallback(() => {
    dragHelpers.dragEnd(setDraggedId, setDragOverId);
  }, []);

  const moveItemWithContext = useCallback((itemId, newParentId, type, map, setMap) => {
    return moveItem(itemId, newParentId, type, map, setMap, projectId, user?.id);
  }, [projectId, user?.id]);

  const dropOnItem = useCallback((e) => {
    if (!authorized) return;
    const item = findItem(itemsMap, dragOverId);
    return dragHelpers.dropOnItem(
      e,
      item,
      draggedId,
      itemsMap,
      setItemsMap,
      moveItemWithContext,
      dragEnd,
      setItemsArr
    );
  }, [authorized, draggedId, dragOverId, itemsMap, dragEnd, moveItemWithContext]);

  const dropInProject = useCallback((e) => {
    if (!authorized) return;
    return dragHelpers.dropInProject(
      e,
      draggedId,
      itemsMap,
      setItemsMap,
      moveItemWithContext,
      dragEnd,
      setItemsArr
    );
  }, [authorized, draggedId, itemsMap, dragEnd, moveItemWithContext]);

  const dropInTabs = useCallback((e, dropIndex = null, pane = 'left') => {
    if (!authorized) return;
    const setPaneActiveTabId = pane === 'left' ? setLeftActiveTabId : setRightActiveTabId;
    return dragHelpers.dropInTabs(e, draggedId, itemsMap, tabs, setTabs, setPaneActiveTabId, dropIndex, pane);
  }, [authorized, draggedId, itemsMap, tabs, setTabs, setLeftActiveTabId, setRightActiveTabId]);

  const dropInEditor = useCallback((e, mode = 'replace', pane = 'left') => {
    if (!authorized) return;
    const setPaneActiveTabId = pane === 'left' ? setLeftActiveTabId : setRightActiveTabId;
    return dragHelpers.dropInEditor(
      e,
      draggedId,
      itemsMap,
      tabs,
      setTabs,
      setPaneActiveTabId,
      mode,
      pane
    );
  }, [authorized, draggedId, itemsMap, tabs, setTabs, setLeftActiveTabId, setRightActiveTabId]);

  return {
    itemsMap,
    setItemsMap,
    itemsArr,
    setItemsArr,
    refresh,
    draggedId,
    dragOverId,
    dragStart,
    dragOver,
    dragEnd,
    dropOnItem,
    dropInProject,
    dropInTabs,
    dropInEditor,
    authorized
  };
}
